import { pdf } from '@react-pdf/renderer';
import { useSnackbar } from 'notistack';
import React from 'react';
import useFetch from 'use-http';
import { apiExports } from 'constants/api';
import { useGlobalSelector } from 'stores';
import { downloadBlob } from 'utils/export';
import { getBase64Image } from 'utils/utilities';
import { SintesiL3Idraulico } from '../pdf';

// Sintesi L3 Idraulico — task-30 (e-2026-06-t30), vedi
// apps/api/plans/features/task-30-handoff-fe-sintesi-cda-l3.md. La lambda di
// export dedicata esiste (`type=115`) ma risponde solo dopo il redeploy
// dell'API — per ora si usa la gemella http (stesso payload, stesso
// metodo BE), commentata la chiamata lambda già pronta per lo scambio
// quando l'ambiente sarà aggiornato: allora basta invertire quale delle due
// righe è commentata, nessun'altra modifica.
const useExportL3Idraulico = ({ id, intCode, setExportSuccess }) => {
  const { get } = useFetch();
  const { enqueueSnackbar } = useSnackbar();
  const { showLoader, hideLoader } = useGlobalSelector();
  const { get: exportLambdaUrl } = useFetch(
    process.env.REACT_APP_EXPORT_LAMBDA_URL
  );

  const date = new Date();
  const currentDate = date.toLocaleDateString().replaceAll('/', '-');
  const currentTime = date.toLocaleTimeString('it-IT').replaceAll(':', '-');

  const pdfL3IdraulicoName = `${intCode} - Sintesi L3 Idraulico - ${currentDate} ${currentTime}`;

  const exportL3Idraulico = async formData => {
    try {
      showLoader();
      // const sintesiL3 = await get(`${apiExports.ROOT}/${id}/hydraulic-l3-cda`);
      const sintesiL3 = await exportLambdaUrl(`/?bridgeId=${id}&type=115`);

      if (sintesiL3) {
        setExportSuccess(true);
      } else {
        setExportSuccess(false);
      }

      // ID275 — url, va convertita. ID18 (fotoCopertina) arriva già in
      // base64 dal BE, nessuna conversione.
      const ortofotoBase64 = sintesiL3?.media?.ortofoto
        ? await getBase64Image(sintesiL3.media.ortofoto)
        : undefined;

      const _pdfData = {
        ...sintesiL3,
        media: {
          ...sintesiL3?.media,
          ortofoto: ortofotoBase64,
        },
      };

      const PDFL3Idraulico = await pdf(
        <SintesiL3Idraulico pdfData={_pdfData} formData={formData} />
      ).toBlob();
      downloadBlob(PDFL3Idraulico, pdfL3IdraulicoName);

      return PDFL3Idraulico;
    } catch (error) {
      console.log(error);
      // Task-30 — 400 (nessuna attività L3 valida) e 403 (modulo L3 non
      // attivo su nessuno dei tre soggetti) sono casi frequenti e meritano
      // un messaggio proprio, non il generico di sotto — stesso codice 403
      // (UserErrors.L3ModuleNotActive) già mappato con testo analogo in
      // DrawerNewInspection.jsx per la creazione di una nuova attività L3.
      if (error?.message?.startsWith('400')) {
        return enqueueSnackbar(
          'Nessuna attività di Livello 3 disponibile per questo ponte',
          { variant: 'error' }
        );
      }
      if (error?.message?.startsWith('403')) {
        return enqueueSnackbar(
          'Per esportare la Sintesi L3 Idraulico è necessario il Modulo L3 attivo. Contattaci per attivarlo.',
          { variant: 'error' }
        );
      }
      return enqueueSnackbar(`Impossibile eseguire l'esportazione`, {
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  return {
    exportL3Idraulico,
    pdfL3IdraulicoName,
  };
};

export default useExportL3Idraulico;
